import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const API_BASE = "http://localhost:4000";

const GoogleDrivePage = ({ embedded = false }) => {
  const [files, setFiles] = useState([]);
  const [driveFiles, setDriveFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);

  // --------------------------------
  // Check login status
  // --------------------------------
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/me`, { credentials: "include" })
      .then((res) => setIsLoggedIn(res.ok))
      .catch(() => setIsLoggedIn(false));
  }, []);

  // --------------------------------
  // Fetch Drive Files on Login
  // --------------------------------
  useEffect(() => {
    if (isLoggedIn) {
      fetchDriveFiles();
    }
  }, [isLoggedIn]);

  // --------------------------------
  // Connect Google Drive
  // --------------------------------
  const connectDrive = () => {
    if (!isLoggedIn) {
      window.location.href = `${API_BASE}/api/auth/google`;
      return;
    }
    window.location.href = `${API_BASE}/api/auth/google/drive`;
  };

  // --------------------------------
  // Fetch Drive Files (for modal)
  // --------------------------------
  const fetchDriveFiles = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/drive/files`, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Drive not connected");

      const data = await res.json();
      setDriveFiles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------
  // Toggle file selection
  // --------------------------------
  const toggleSelect = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  // --------------------------------
  // Import selected files
  // --------------------------------
  const importSelected = async () => {
    if (selectedFiles.length === 0) return;

    const filesToImport = driveFiles.filter((f) => selectedFiles.includes(f.id));
    setUploadingFiles(filesToImport);

    try {
      setImporting(true);
      setUploadProgress(0);

      const interval = setInterval(() => {
        setUploadProgress((prev) => (prev >= 90 ? prev : prev + 10));
      }, 300);

      await fetch(`${API_BASE}/api/drive/import`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileIds: selectedFiles,
        }),
      });

      clearInterval(interval);
      setUploadProgress(100);
      setSelectedFiles([]);
      toast.success("Files imported successfully");
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setTimeout(() => {
        setImporting(false);
        setUploadProgress(0);
        setUploadingFiles([]);
      }, 1000);
    }
  };

  // --------------------------------
  // Logout
  // --------------------------------
  const logout = async () => {
    await fetch(`${API_BASE}/api/logout`, {
      method: "POST",
      credentials: "include",
    });
    setIsLoggedIn(false);
    setFiles([]);
  };

  return (
    <div
      style={{
        ...styles.page,
        minHeight: embedded ? "auto" : "100vh",
        height: embedded ? "auto" : "auto",
        padding: embedded ? "8px 0 0 0" : styles.page.padding,
      }}
      className="app-theme-bg"
    >
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={{ margin: 0, color: "var(--text-main)" }}>Google Drive</h2>
          <small style={{ color: "var(--text-secondary)" }}>
            Status: {isLoggedIn ? "Logged In" : "Not Logged In"}
          </small>
        </div>

        <div style={styles.actions}>
          <button onClick={connectDrive} style={styles.primaryBtn}>
            {isLoggedIn ? "Connect Drive" : "Login with Google"}
          </button>

          {selectedFiles.length > 0 && (
            <button
              onClick={importSelected}
              disabled={importing}
              style={styles.primaryBtn}
            >
              {importing ? `Importing...` : `Import (${selectedFiles.length})`}
            </button>
          )}

          <button onClick={logout} style={styles.dangerBtn}>
            Logout
          </button>
        </div>
      </div>

      {/* Progress Modal */}
      {importing && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>Importing Files...</h3>
            <div style={styles.progressContainer}>
              <div
                style={{ ...styles.progressBar, width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div
              style={{
                textAlign: "right",
                fontSize: "12px",
                marginTop: 4,
                color: "#666",
              }}
            >
              {uploadProgress}%
            </div>
            <div style={styles.uploadList}>
              {uploadingFiles.map((file) => (
                <div key={file.id} style={styles.uploadItem}>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "75%",
                    }}
                  >
                    {file.name}
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: uploadProgress === 100 ? "#10b981" : "#6b7280",
                    }}
                  >
                    {uploadProgress === 100 ? "Done" : "Uploading"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && <p style={styles.error}>{error}</p>}

      {/* File List */}
      {loading ? (
        <p>Loading files...</p>
      ) : (
        <div style={styles.fileList}>
          {driveFiles.map((file) => (
            <div key={file.id} style={styles.fileRow}>
              <input
                type="checkbox"
                checked={selectedFiles.includes(file.id)}
                onChange={() => toggleSelect(file.id)}
              />
              <span
                style={{ marginLeft: 8, cursor: "pointer", fontWeight: 500 }}
                onClick={() => window.open(file.webViewLink, "_blank")}
              >
                {file.name}
              </span>
              <small
                style={{ marginLeft: "auto", color: "#888", marginRight: 10 }}
              >
                {file.mimeType}
              </small>
              <button
                style={styles.viewBtn}
                onClick={() => window.open(file.webViewLink, "_blank")}
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* -------------------------------
   Styles
-------------------------------- */
const styles = {
  page: {
    padding: 24,
    fontFamily: "Inter, sans-serif",
    minHeight: "100vh",
    backgroundColor: "var(--bg-main)",
    color: "var(--text-main)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  actions: {
    display: "flex",
    gap: 8,
  },
  primaryBtn: {
    background: "var(--primary-color, #4f46e5)",
    color: "var(--btn-primary-text, #fff)",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "#fff",
    border: "1px solid #ddd",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
  },
  dangerBtn: {
    background: "var(--danger-color, #ef4444)",
    color: "var(--btn-danger-text, #fff)",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    cursor: "pointer",
  },
  error: {
    color: "#dc2626",
    marginBottom: 12,
  },
  fileList: {
    marginTop: 12,
    border: "1px solid #eee",
    borderRadius: 6,
    background: "var(--bg-surface)",
    borderColor: "var(--border-color)",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    padding: 10,
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-main)",
  },
  viewBtn: {
    background: "transparent",
    border: "1px solid var(--primary-color, #4f46e5)",
    color: "var(--primary-color, #4f46e5)",
    borderRadius: 4,
    padding: "4px 8px",
    cursor: "pointer",
    fontSize: "12px",
  },
  progressContainer: {
    width: "100%",
    height: "6px",
    backgroundColor: "var(--light-gray, #e5e7eb)",
    borderRadius: "4px",
    marginBottom: "16px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "var(--primary-color, #4f46e5)",
    transition: "width 0.3s ease",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "var(--bg-surface)",
    padding: "24px",
    borderRadius: "8px",
    width: "400px",
    maxWidth: "90%",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  uploadList: {
    marginTop: "16px",
    maxHeight: "200px",
    overflowY: "auto",
    borderTop: "1px solid var(--border-color)",
  },
  uploadItem: {
    padding: "8px 0",
    borderBottom: "1px solid var(--border-color)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "14px",
  },
};

export default GoogleDrivePage;